import type { ReadonlyRef, RefOrGetter } from '@/helpers'
import {
    type Coordinate,
    createEmptyCoordinate,
    type DragCallbackEvent,
    type DragState,
    subtractCoordinate,
} from '@/composables/motion/drag-pincher.ts'
import { type Ref, ref, toValue, watch } from 'vue'
import { getImageConstructor } from '@/helpers/globals.ts'

export interface MouseDrag {
    drag: ReadonlyRef<DragState>
    bound: ReadonlyRef<boolean>
}

export function useMouseDrag(options: {
    container: RefOrGetter<HTMLElement | null>
    callback: (container: HTMLElement, e: DragCallbackEvent) => void
}): MouseDrag {
    const { container: containerRef, callback } = options
    const bound = ref(false)

    const drag: Ref<DragState> = ref({
        current: createEmptyCoordinate(),
        start: createEmptyCoordinate(),
        delta: createEmptyCoordinate(),
        deltaPrevious: createEmptyCoordinate(),
    })

    function handleDragCallback(dragging: boolean): void {
        const container = toValue(containerRef)

        if (!container) {
            return
        }

        callback(container, {
            ...drag.value,
            dragging,
        })
    }

    const smallestImage =
        'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='

    function onDragStart(e: DragEvent): void {
        e.stopPropagation()

        const Image = getImageConstructor()

        if (Image) {
            const di = new Image()
            di.src = smallestImage

            if (e.dataTransfer) {
                e.dataTransfer.setDragImage(di, 10, 10)
                // Run in firefox
                e.dataTransfer.setData('text/plain', '')
                e.dataTransfer.effectAllowed = 'move'
            }
        }

        const coordinate: Coordinate = {
            x: e.clientX,
            y: e.clientY,
        }

        drag.value = {
            ...drag.value,
            start: coordinate,
            current: coordinate,
        }

        handleDragCallback(true)
    }

    function onDrag(e: DragEvent): void {
        if (0 === e.screenX) {
            // Prevents a bug
            return
        }

        const current: Coordinate = {
            x: e.clientX,
            y: e.clientY,
        }

        const previous = drag.value

        const delta = subtractCoordinate(current, previous.start)
        const deltaPrevious = subtractCoordinate(current, previous.current)

        drag.value = {
            current: current,
            start: previous.start,
            delta: delta,
            deltaPrevious: deltaPrevious,
        }

        handleDragCallback(true)
    }

    function onDragOver(e: DragEvent): void {
        e.preventDefault()
    }

    function onDragEnd(): void {
        handleDragCallback(false)

        drag.value = {
            current: createEmptyCoordinate(),
            start: createEmptyCoordinate(),
            delta: createEmptyCoordinate(),
            deltaPrevious: createEmptyCoordinate(),
        }
    }

    watch(containerRef, (container, oldContainer) => {
        if (container) {
            container.addEventListener('dragstart', onDragStart, {
                passive: true,
            })
            container.addEventListener('drag', onDrag, {
                passive: true,
            })
            container.addEventListener('dragover', onDragOver)
            container.addEventListener('dragend', onDragEnd, {
                passive: true,
            })
        }

        bound.value = !!container

        if (oldContainer) {
            oldContainer.removeEventListener('dragstart', onDragStart)
            oldContainer.removeEventListener('drag', onDrag)
            oldContainer.removeEventListener('dragover', onDragOver)
            oldContainer.removeEventListener('dragend', onDragEnd)
        }
    })

    return {
        drag,
        bound,
    }
}
